import moment from 'moment-timezone';
import { API_DATE_FORMAT } from "../constants";
import { timeToSeconds, secondsToTime, graphDateString } from "../helpers/moment-ex";
import { groupBy, isToday, isElasticLoad, isCrewView, isFarmView, isEGView } from '../helpers/common';
import { toMilesPerHour } from '../helpers/unit';

export const getEGSpeed = response => {
    const gpsPoints = {};
    let avg_speed = 0, max_speed = 0, totalSpeed = 0;

    for (const gpsList of response) {
        for (const egId in gpsList) {
            if (!egId) return;
            if (!(gpsList[egId].hasOwnProperty("data")
                && Array.isArray(gpsList[egId].data)
                && gpsList[egId].data.length > 0)) continue;

            for (const item of gpsList[egId].data) {
                if (!item.speed) continue;
                if (!gpsPoints[egId]) gpsPoints[egId] = [];
                gpsPoints[egId].push({
                    egId, ts: item.ts,
                    speed: toMilesPerHour(item.speed)
                });
            }
        }
    }

    for (const egId in gpsPoints) {
        totalSpeed = gpsPoints[egId].reduce((total, obj) => obj.speed + total, 0);
        avg_speed = (totalSpeed / gpsPoints[egId].length);
        max_speed = Math.max(...gpsPoints[egId].map(item => item.speed));
    }

    return { avg_speed, max_speed };
}

export const isMergeElasticData = (state, key) => {
    if (!(state.elastic && state.elastic[key] && state.elastic[key].data)) return;
    const item = state.elastic[key];
    const search = state.searchParams;
    if (search.range !== item.range) return;

    let deviceId = null;
    const analyze = state.analyzeParams;
    if (isEGView(analyze.view)) deviceId = (key === "load" && item.isLive) ? analyze.crewId : analyze.egId;
    else if (isCrewView(analyze.view)) deviceId = analyze.crewId;
    else if (isFarmView(analyze.view)) deviceId = analyze.farmId;
    return (item.deviceId === deviceId);
}

export const mergeLoad = (state, payload) => {
    if (!payload) return;
    if (!isElasticLoad()) return;

    const { isLive, data } = payload;
    return isLive ? liveLoad(state, data) : apiLoad(state, data);
};

const liveLoad = (state, message) => {
    if (!message) return;
    const { view } = state.analyzeParams;

    //Farm load
    if (isFarmView(view)) {
        let loadCount = 0;
        if (message.hasOwnProperty("load_count_estimate")) loadCount = parseInt(message.load_count_estimate);
        else if (message.hasOwnProperty("load_count")) loadCount = parseInt(message.load_count);
        if (loadCount <= 0) return;
        return { summaryInfo: { ...state.summaryInfo, load: { total_loads: loadCount } } };
    }

    // Trips calculation
    if (!(message.equipment_groups && Array.isArray(message.equipment_groups))) return;
    const { isTruck, egId } = state.analyzeParams;
    const egList = Array.isArray(message.equipment_groups) ? message.equipment_groups : [];
    let sumOfRoundTripSeconds = 0, sumOfLoadCount = 0, egCount = 0;
    let egLoadCount = 0, egAvgRoundTripSeconds = 0;

    for (const item of egList) {
        if (!Array.isArray(item.trips)) continue;
        const trips = item.trips.sort((a, b) => a.trip_id - b.trip_id);
        let prevStart = null, roundTripSeconds = 0;

        for (const trip of trips) {
            const currentStart = moment.utc(trip.start_time);
            roundTripSeconds += prevStart ? currentStart.diff(prevStart, "seconds") : 0;
            prevStart = currentStart;
        }

        if (item.equipment_group_uuid === egId) {
            egLoadCount = trips.length;
            egAvgRoundTripSeconds = (egLoadCount > 0) ? (roundTripSeconds / egLoadCount) : 0;
        }

        egCount += (trips.length > 0) ? 1 : 0;
        sumOfLoadCount += trips.length;
        sumOfRoundTripSeconds += (trips.length > 0) ? (roundTripSeconds / trips.length) : 0;
    }

    const crewAvgLoads = (egCount > 0) ? Math.ceil(sumOfLoadCount / egCount) : 0;
    const crewAvgRoundTripSeconds = (egCount > 0) ? (sumOfRoundTripSeconds / egCount) : 0;
    const crewAvgRoundTripTime = secondsToTime(crewAvgRoundTripSeconds);
    const egAvgRoundTripTime = secondsToTime(egAvgRoundTripSeconds);

    const prevLoad = state.summaryInfo.load;
    const prevTotalLoads = prevLoad && prevLoad.total_loads ? prevLoad.total_loads : 0;
    const prevAvgRoundTripLoadTime = prevLoad && prevLoad.avg_roundtrip_load_time ? prevLoad.avg_roundtrip_load_time : secondsToTime(0);

    //Crew load
    if (isCrewView(view)) {
        return {
            summaryInfo: {
                ...state.summaryInfo,
                load: {
                    total_loads: (sumOfLoadCount > 0)
                        ? sumOfLoadCount
                        : prevTotalLoads,
                    avg_roundtrip_load_time: (crewAvgRoundTripSeconds > 0)
                        ? crewAvgRoundTripTime
                        : prevAvgRoundTripLoadTime
                }
            }
        }
    }
    //Harvester load
    else if (isEGView(view) && !isTruck) {
        return {
            summaryInfo: {
                ...state.summaryInfo,
                load: {
                    total_loads: (egLoadCount > 0)
                        ? egLoadCount
                        : prevTotalLoads,
                    avg_roundtrip_load_time: (egAvgRoundTripSeconds > 0)
                        ? egAvgRoundTripTime
                        : prevAvgRoundTripLoadTime
                }
            }
        }
    }
    //Truck load
    else if (isEGView(view) && isTruck) {
        const { loadInfo } = state;
        return {
            loadInfo: {
                ...loadInfo,
                crew: {
                    ...loadInfo.crew,
                    ...(crewAvgLoads > 0) && { avg_loads: crewAvgLoads },
                    ...(crewAvgRoundTripSeconds > 0) && { avg_load_time: crewAvgRoundTripTime }
                },
                vehicle: {
                    ...loadInfo.vehicle,
                    ...(egLoadCount > 0) && { loads: egLoadCount },
                    ...(egAvgRoundTripSeconds > 0) && { avg_round_trip_load_time: egAvgRoundTripTime }
                }
            }
        };
    }
};

const apiLoad = (state, message) => {
    if (!(message && message.data)) return;

    const { deviceId } = state.elastic.load;
    if (!message.data[deviceId]) return;

    const { view, isTruck } = state.analyzeParams;
    const load = message.data[deviceId];
    const loadCount = load.hasOwnProperty("load_count") ? parseInt(load.load_count) : 0;
    const avgRoundTripSeconds = load.hasOwnProperty("round_trip_seconds") ? parseFloat(load.round_trip_seconds) : 0;
    const avgRoundTripTime = secondsToTime(avgRoundTripSeconds);

    //Farm load
    if (isFarmView(view)) {
        if (loadCount <= 0) return;
        return { summaryInfo: { ...state.summaryInfo, load: { total_loads: loadCount } } };
    }
    //Truck load
    else if (isEGView(view) && isTruck) {
        const { loadInfo } = state;
        return {
            loadInfo: {
                ...loadInfo,
                crew: {
                    ...loadInfo.crew,
                    avg_loads: 0,
                    avg_load_time: secondsToTime(0)
                },
                vehicle: {
                    ...loadInfo.vehicle,
                    ...(loadCount > 0) && { loads: loadCount },
                    ...(avgRoundTripSeconds > 0) && { avg_round_trip_load_time: avgRoundTripTime }
                }
            }
        };
    }
    //Crew and Harvester load
    else {
        const prevLoad = state.summaryInfo.load;
        const prevTotalLoads = prevLoad && prevLoad.total_loads ? prevLoad.total_loads : 0;
        const prevAvgRoundTripLoadTime = prevLoad && prevLoad.avg_roundtrip_load_time ? prevLoad.avg_roundtrip_load_time : secondsToTime(0);

        return {
            summaryInfo: {
                ...state.summaryInfo,
                load: {
                    total_loads: (loadCount > 0)
                        ? loadCount
                        : prevTotalLoads,
                    avg_roundtrip_load_time: (avgRoundTripSeconds > 0)
                        ? avgRoundTripTime
                        : prevAvgRoundTripLoadTime
                }
            }
        };
    }
};

export const mergeSummaryKPI = (state, payload, initialState) => {
    if (!(payload && payload.data)) return;

    const { view } = state.analyzeParams;
    const { isLive, data } = payload;
    const message = { ...data };
    let completedAcres = null, completedSeconds = null;

    if (isLive) {
        completedAcres = message.summary && message.summary.work && !isNaN(message.summary.work.acres) ? message.summary.work.acres : null;
        completedSeconds = message.work_day && !isNaN(message.work_day.seconds) ? message.work_day.seconds : null;
    }
    else {
        //Farm KPI
        const farmId = state.analyzeParams.farmId;
        if (isFarmView(view) && !(farmId && message[farmId])) return;

        // Crew KPI
        const crewId = state.analyzeParams.crewId;
        if (isCrewView(view) && !(crewId && message[crewId])) return;

        const data = getCompleted(message);
        completedAcres = data.completedAcres;
        completedSeconds = data.completedSeconds;
    }

    if (completedAcres === null) return;

    //completed acres
    let acres = null;
    if (isLive ? completedAcres > 0 : true) {
        acres = state.summaryInfo.acres ? state.summaryInfo.acres : initialState.summaryInfo.acres;
        acres = {
            ...acres,
            completed: completedAcres,
            remaining: (acres.planned > completedAcres) ? (acres.planned - completedAcres) : 0
        }
    }

    //completed hours
    let hours = null;
    if (!isFarmView(view) && completedSeconds > 0) {
        hours = state.summaryInfo.hours ? state.summaryInfo.hours : initialState.summaryInfo.hours;

        const plannedSeconds = timeToSeconds(hours.planned);
        hours = {
            ...hours,
            completed: secondsToTime(completedSeconds),
            remaining: (plannedSeconds > completedSeconds) ? secondsToTime(plannedSeconds - completedSeconds) : 0
        }
    }

    return {
        summaryInfo: {
            ...state.summaryInfo,
            ...acres ? { acres } : {},
            ...hours ? { hours } : {}
        }
    };
};

const getCompleted = message => {
    let completedAcres = 0, completedSeconds = 0;

    for (const key in message) {
        if (!(message[key].data && Array.isArray(message[key].data))) continue;
        const data = message[key].data;

        for (const item of data) {
            completedAcres += item.summary && item.summary.work && !isNaN(item.summary.work.acres) ? item.summary.work.acres : 0;
            completedSeconds += item.work_day && !isNaN(item.work_day.seconds) ? item.work_day.seconds : 0;
        }
    }

    return { completedAcres, completedSeconds };
};

export const mergeDonutKPI = (state, payload) => {
    if (!(payload && payload.data && state.analyzeParams.tab === 1)) return;

    const { view } = state.analyzeParams;
    const { isLive, data } = payload;
    const message = { ...data };

    let { donutInfo } = state;
    let totalDuration = donutInfo.total_duration;
    let utilization = 0, actualWorkSeconds = 0, actualUtilizationSeconds = 0;

    if (isLive && message.summary && message.summary.work) {
        if (totalDuration.h === 0) totalDuration = { h: 24, m: 0 };
        utilization = message.summary.work.utilization;
        actualWorkSeconds = (message.work_day && message.work_day.seconds) ? message.work_day.seconds : 0;

        const utilizationSecondsKey = isEGView(view) ? "seconds" : "average_utilization_seconds";
        actualUtilizationSeconds = !isNaN(message.summary.work[utilizationSecondsKey]) ? message.summary.work[utilizationSecondsKey] : 0;

    } else {
        const worked = getUtilization(message, view);
        utilization = worked.utilization;
        actualWorkSeconds = worked.actualWorkSeconds;
        actualUtilizationSeconds = worked.actualUtilizationSeconds;
    }

    donutInfo = {
        ...donutInfo,
        utilization: (utilization <= 100 ? utilization : 100),
        actual_work: secondsToTime(actualWorkSeconds),
        actual_utilization: secondsToTime(actualUtilizationSeconds),
        actual_work_percentage: convertPerc(actualWorkSeconds, totalDuration),
        actual_utilization_percentage: convertPerc(actualUtilizationSeconds, totalDuration)
    };

    return { donutInfo };
};

const getUtilization = (message, view) => {
    const utilizationSecondsKey = isEGView(view) || isFarmView(view) ? "seconds" : "average_utilization_seconds";
    let utilization = 0, actualWorkSeconds = 0, actualUtilizationSeconds = 0;
    let totalUtilization = 0, activeCount = 0;

    for (const key in message) {
        if (!(message[key].data && Array.isArray(message[key].data))) continue;
        const data = message[key].data;
        let utilizationOfWorking = 0, workingDays = 0;

        for (const item of data) {
            //acutal work seconds
            actualWorkSeconds += (item.work_day && !isNaN(item.work_day.seconds)) ? item.work_day.seconds : 0;
            if (!item.summary && item.summary.work) continue;

            //actual utilization seconds
            actualUtilizationSeconds += !isNaN(item.summary.work[utilizationSecondsKey]) ? item.summary.work[utilizationSecondsKey] : 0;

            //utilization
            const utilizationPerDay = !isNaN(item.summary.work.utilization) ? item.summary.work.utilization : 0;
            utilizationOfWorking += utilizationPerDay;
            workingDays += (utilizationPerDay > 0) ? 1 : 0;
        }

        utilizationOfWorking = (workingDays > 0) ? (utilizationOfWorking / workingDays) : 0;
        totalUtilization += utilizationOfWorking;
        activeCount += (utilizationOfWorking > 0) ? 1 : 0;
    }
    utilization = (activeCount > 0) ? (totalUtilization / activeCount) : 0;

    return { utilization, actualWorkSeconds, actualUtilizationSeconds };
};

export const mergeGraphKPI = (state, payload, initialState, timezone) => {
    if (!(payload && payload.data && state.analyzeParams.tab === 2)) return;

    const { view } = state.analyzeParams;
    const { range } = state.searchParams;
    const { isLive, data, deviceId } = payload;
    let message = { ...data };

    let graphInfo = {};
    if (isToday(range)) {
        if (!isLive) {
            message = data[deviceId] && Array.isArray(data[deviceId].data) &&
                data[deviceId].data.length > 0 ? data[deviceId].data[0] : null;
        }

        graphInfo = isEGView(view) ?
            todayGraphForEG(state, message, timezone) :
            todayGraphForFarmAndCrew(state, message, timezone);
    }
    else {
        graphInfo = historicalGraphForAll(state, message, timezone);
    }

    return { graphInfo: { ...initialState.graphInfo, ...state.graphInfo, ...graphInfo } };
};

const todayGraphForEG = (state, message, timezone) => {
    if (!(message && message.work && message.work_day && message.work_day.start_time)) return;
    let actualLine = [], trendLine = [], plannedLine = [];

    const { graphInfo } = state;
    const workDayStart = moment.utc(message.work_day.start_time).tz(timezone);
    const workDayEnd = moment.utc(message.work_day.end_time).tz(timezone);
    const startOfDay = workDayStart.clone().startOf('day');
    const endOfDay = workDayStart.clone().endOf('day').add("seconds", 1);

    //-------------Planned line calculation----------------------
    const plannedActivity = graphInfo.planned_activity;
    if (plannedActivity.length > 0) {
        const plannedAcres = plannedActivity[0].acres;
        plannedLine.push({ time: startOfDay, acres: 0, ts: startOfDay.valueOf() });
        plannedLine.push({ time: workDayStart, acres: 0, ts: workDayStart.valueOf() });
        plannedLine.push({ time: workDayEnd, acres: plannedAcres, ts: workDayEnd.valueOf() });
        plannedLine.push({ time: endOfDay, acres: plannedAcres, ts: endOfDay.valueOf() });
        plannedLine = plannedLine.map(e => [graphDateString(e.time), e.acres]);
    }
    //-----------------------------------------------------------

    //-------------Actual line calculation-----------------------
    let actualAcres = 0, actualEndTime = null;
    if (message.work.length > 0) {
        actualLine.push({ time: startOfDay, acres: 0, ts: startOfDay.valueOf() });
        actualLine.push({ time: workDayStart, acres: 0, ts: workDayStart.valueOf() });

        for (let item of message.work) {
            if (!item.gps) continue;

            for (let gpsItems of item.gps) {
                if (!gpsItems.from_last_gps) continue;

                const time = moment.utc(gpsItems.time).tz(timezone);
                if (!(workDayStart <= time && workDayEnd >= time)) continue;
                actualLine.push({ time, acres: gpsItems.from_last_gps.acres, ts: time.valueOf() });
            }
        }
        actualLine = actualLine.sort((a, b) => a.ts - b.ts);
        actualEndTime = actualLine[actualLine.length - 1].time;

        actualLine = actualLine.map(e => {
            actualAcres += e.acres;
            return [graphDateString(e.time), actualAcres];
        });
    }
    //----------------------------------------------------------

    //-------------Trend line calculation-----------------------
    if (actualEndTime) {
        const trendEndTime = endOfDay;
        const remainingSecondsOfDay = trendEndTime.diff(actualEndTime, 'seconds');

        if (remainingSecondsOfDay > 0) {
            const workSeconds = message.summary && message.summary.work && !isNaN(message.summary.work.seconds) ? message.summary.work.seconds : 0;
            const acresPerSecond = (workSeconds > 0) ? (actualAcres / workSeconds) : 0;
            const trendAcres = (remainingSecondsOfDay * acresPerSecond);
            trendLine.push({ time: actualEndTime, acres: actualAcres, ts: actualEndTime.valueOf() });
            trendLine.push({ time: trendEndTime, acres: trendAcres, ts: trendEndTime.valueOf() });
        }

        let trendAcres = 0;
        trendLine = trendLine.sort((a, b) => a.ts - b.ts).map(e => {
            trendAcres += e.acres;
            return [graphDateString(e.time), trendAcres];
        });
    }
    //---------------------------------------------------------

    return {
        actual: actualLine, trend: trendLine,
        ...(plannedLine.length > 0) ? { planned: plannedLine } : {}
    };
}

const todayGraphForFarmAndCrew = (state, message, timezone) => {
    if (!(message && message.work_day && message.work_day.start_time)) return;
    if (!(message.detail && Array.isArray(message.detail.work) && message.detail.work.length > 0)) return;
    let actualLine = [], trendLine = [], plannedLine = [];

    const { graphInfo } = state;
    const workDayStart = moment.utc(message.work_day.start_time).tz(timezone);
    const workDayEnd = moment.utc(message.work_day.end_time).tz(timezone);
    const startOfDay = workDayStart.clone().startOf('day');
    const endOfDay = workDayStart.clone().endOf('day').add("seconds", 1);

    //----------------Planned line calculation----------------------
    const plannedActivity = graphInfo.planned_activity;
    if (plannedActivity.length > 0) {
        const plannedAcres = plannedActivity[0].acres;
        plannedLine.push({ time: startOfDay, acres: 0, ts: startOfDay.valueOf() });
        plannedLine.push({ time: workDayStart, acres: 0, ts: workDayStart.valueOf() });
        plannedLine.push({ time: workDayEnd, acres: plannedAcres, ts: workDayEnd.valueOf() });
        plannedLine.push({ time: endOfDay, acres: plannedAcres, ts: endOfDay.valueOf() });
        plannedLine = plannedLine.map(e => [graphDateString(e.time), e.acres]);
    }
    //--------------------------------------------------------------

    //----------------Actual line calculation-----------------------
    let actualAcres = 0, actualEndTime = null;
    if (message.detail.work.length > 0) {
        actualLine.push({ time: startOfDay, acres: actualAcres, ts: 0 });
        for (const item of message.detail.work) {
            const startTime = moment.utc(item.start).tz(timezone);
            const endTime = moment.utc(item.end).tz(timezone);
            if (!(workDayStart <= startTime && workDayEnd >= endTime)) continue;

            actualLine.push({ time: startTime, acres: actualAcres, ts: item.ts });
            actualAcres += item.acres;
            actualLine.push({ time: endTime, acres: actualAcres, ts: item.ts });
        }

        actualLine = actualLine.sort((a, b) => a.ts - b.ts);
        actualEndTime = actualLine[actualLine.length - 1].time;
        actualLine = actualLine.map(e => [graphDateString(e.time), e.acres]);
    }
    //---------------------------------------------------------------

    //-------------Trend line calculation----------------------------
    if (actualEndTime) {
        const trendEndTime = endOfDay;
        const remainingSecondsOfDay = trendEndTime.diff(actualEndTime, 'seconds');

        if (remainingSecondsOfDay > 0) {
            const workSeconds = message.summary && message.summary.work && !isNaN(message.summary.work.seconds) ? message.summary.work.seconds : 0;
            const acresPerSecond = (workSeconds > 0) ? (actualAcres / workSeconds) : 0;
            const trendAcres = (remainingSecondsOfDay * acresPerSecond);
            trendLine.push({ time: actualEndTime, acres: actualAcres, ts: actualEndTime.valueOf() });
            trendLine.push({ time: trendEndTime, acres: trendAcres, ts: trendEndTime.valueOf() });
        }

        let trendAcres = 0;
        trendLine = trendLine.sort((a, b) => a.ts - b.ts).map(e => {
            trendAcres += e.acres;
            return [graphDateString(e.time), trendAcres];
        });
    }
    //--------------------------------------------------------------

    return {
        actual: actualLine, trend: trendLine,
        ...(plannedLine.length > 0) ? { planned: plannedLine } : {}
    };
}

const historicalGraphForAll = (state, message, timezone) => {
    const { graphInfo } = state;
    const { view } = state.analyzeParams;
    const planStartTime = (graphInfo.planned.length > 0) ? graphInfo.planned[0][0] : graphInfo.start_date;
    const isWorkInterval = isCrewView(view) || isEGView(view);

    const getWorkDate = (start, end) => {
        if (!(start && end)) return;
        const startTime = moment.utc(start).tz(timezone);
        const endTime = moment.utc(end).tz(timezone);

        return {
            startTime, endTime, ts: startTime.valueOf(),
            day: startTime.clone().startOf('day').valueOf()
        };
    }

    //---------------------Find work window------------------------
    let workWindow = [], workDetail = [];
    for (const key in message) {
        if (!(message[key].data && Array.isArray(message[key].data))) continue;
        const data = message[key].data;

        for (const item of data) {
            if (isWorkInterval) {
                let detailWork = [];
                if (isEGView(view)) {
                    detailWork = (item.work && Array.isArray(item.work)) ? item.work : [];
                    for (const work of detailWork) {
                        if (!work.gps) continue;
                        for (const gpsItem of work.gps) {
                            if (!gpsItem.from_last_gps) continue;
                            const time = moment.utc(gpsItem.time).tz(timezone);
                            workDetail.push({ time, acres: gpsItem.from_last_gps.acres, ts: time.valueOf() });
                        }
                    }
                } else {
                    detailWork = (item.detail && item.detail.work && Array.isArray(item.detail.work)) ? item.detail.work : [];
                    for (const work of detailWork) {
                        const date = getWorkDate(work.start, work.end);
                        workDetail.push({ ...date, acres: work.acres });
                    }
                }
            }

            if (!(item.work_day && item.work_day.start_time && item.work_day.end_time)) continue;
            const workDay = getWorkDate(item.work_day.start_time, item.work_day.end_time);
            workWindow.push(workDay);

            if (!isWorkInterval) {
                const acres = (item.summary && item.summary.work && !isNaN(item.summary.work.acres)) ? item.summary.work.acres : 0;
                workDetail.push({ ...workDay, acres });
            }
        }
    }

    workWindow = workWindow.sort((a, b) => a.ts - b.ts);
    workDetail = workDetail.sort((a, b) => a.ts - b.ts);
    //--------------------------------------------------------------

    //---------------------Find work activity-----------------------
    let workActivity = [];
    if (isWorkInterval) workActivity = workDetail;
    else {
        const detailGrouping = groupBy('day')(workDetail);
        for (const day in detailGrouping) {
            const list = detailGrouping[day].sort((a, b) => a.day - b.day);
            let minStarTime = null, maxEndTime = null, acres = 0;

            for (const item of list) {
                if (!minStarTime || minStarTime > item.startTime) minStarTime = item.startTime;
                if (!maxEndTime || maxEndTime < item.endTime) maxEndTime = item.endTime;
                acres += item.acres;
            }

            workActivity.push({
                startTime: minStarTime, endTime: maxEndTime,
                acres, day, ts: minStarTime.valueOf()
            });
        }
    }
    //---------------------------------------------------------------

    //----------------Planned line calculation-----------------------
    let planned = graphInfo.planned, plannedLine = [], plannedAcres = 0, index = 0;
    const plannedActivity = graphInfo.planned_activity;

    for (const item of plannedActivity) {
        let startTime = moment.utc(item.start_time);
        let endTime = moment.utc(item.end_time);

        const startDateStr = startTime.format(API_DATE_FORMAT);
        const findItems = workWindow.filter(e => e.startTime.format(API_DATE_FORMAT) === startDateStr);
        if (findItems.length > 0) {
            startTime = moment.utc(graphDateString(findItems[0].startTime));
            endTime = moment.utc(graphDateString(findItems[0].endTime));
        }

        if (index === 0) {
            const start = (planned.length > 0) ? moment.utc(planned[0][0]) : moment.utc(item.start_time);
            plannedLine.push({ time: graphDateString(start), acres: plannedAcres, ts: start.valueOf() });
        }

        plannedLine.push({ time: graphDateString(startTime), acres: plannedAcres, ts: startTime.valueOf() });
        plannedAcres += item.acres;
        plannedLine.push({ time: graphDateString(endTime), acres: plannedAcres, ts: endTime.valueOf() });

        if (index === plannedActivity.length - 1) {
            const end = (planned.length > 0) ? moment.utc(planned[planned.length - 1][0]) : moment.utc(item.end_time);
            plannedLine.push({ time: graphDateString(end), acres: plannedAcres, ts: end.valueOf() });
        }
        index++;
    }
    plannedLine = plannedLine.sort((a, b) => a.ts - b.ts).map(e => [e.time, e.acres]);
    //----------------------------------------------------------------

    //----------------Actual line calculation-------------------------
    let actualLine = [], totalAcres = 0;
    if (planStartTime && workActivity.length > 0) {
        actualLine.push({ time: planStartTime, acres: totalAcres, ts: 0 });

        if (isEGView(view)) {
            for (const item of workActivity) {
                actualLine.push({ time: graphDateString(item.time), acres: totalAcres, ts: item.ts });
                totalAcres += item.acres;
            }
        }
        else {
            for (const item of workActivity) {
                actualLine.push({ time: graphDateString(item.startTime), acres: totalAcres, ts: item.ts });
                totalAcres += item.acres;
                actualLine.push({ time: graphDateString(item.endTime), acres: totalAcres, ts: item.ts });
            }
        }
        actualLine = actualLine.sort((a, b) => a.ts - b.ts).map(e => [e.time, e.acres]);
    }

    if (actualLine.length > 0 && plannedLine.length > 0) {
        const planEndTime = plannedLine[plannedLine.length - 1][0];
        const planEndUTCTime = moment.utc(planEndTime);

        const actualEndTime = actualLine[actualLine.length - 1][0];
        const actualEndUTCTime = moment.utc(actualEndTime);

        if (planEndUTCTime < actualEndUTCTime) {
            const plannedAcres = plannedLine[plannedLine.length - 1][1];
            plannedLine.push([actualEndTime, plannedAcres]);
        } else if (planEndUTCTime > actualEndUTCTime) {
            const actualAcres = actualLine[actualLine.length - 1][1];
            actualLine.push([planEndTime, actualAcres]);
        }
    }
    //-----------------------------------------------------------------

    return { planned: plannedLine, actual: actualLine, trend: [] };
}

const convertPerc = (seconds, totalTime) =>
    parseFloat(((seconds / timeToSeconds(totalTime)) * 100).toFixed(2));

export const parseBaleKPI = (data, egId) => {
    const bales = data && data[egId] && Array.isArray(data[egId].data) ? data[egId].data : [];
    let totalBaleSeconds = 0, baleCount = 0;

    for (const e of bales) {
        if (!(e.bale_finished_ts && e.bale_start_ts)) continue;
        totalBaleSeconds += (e.bale_finished_ts - e.bale_start_ts) / 1000;
        baleCount++;
    }

    const avgBaleTime = secondsToTime(baleCount > 0 ? totalBaleSeconds / baleCount : 0);
    return { avg_bale_time: avgBaleTime, bales: baleCount };
};